import { lumi } from "@/lib/lumi"
import { useState } from "react"

export interface Article {
  _id: string
  title: string
  excerpt: string
  content: string
  category: string
  iconType: string
  color: string
  bgColor: string
  published: boolean
  creator: string
  createdAt: string
  updatedAt: string
}

export function useArticles() {
  const [articles, setArticles] = useState<Article[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)

  const fetchArticles = async (options?: {
    filter?: Record<string, any>
    sort?: Record<string, 1 | -1>
    limit?: number
    skip?: number
    search?: string
    append?: boolean
  }) => {
    setLoading(true)
    try {
      let filter: Record<string, any> = { published: true, ...options?.filter }
      
      // Add search filter if search term provided
      if (options?.search) {
        filter = {
          ...filter,
          $or: [
            { title: { $regex: options.search, $options: 'i' } },
            { excerpt: { $regex: options.search, $options: 'i' } },
            { content: { $regex: options.search, $options: 'i' } }
          ]
        }
      }
      
      const { list, total } = await lumi.entities.articles.list({
        filter,
        sort: options?.sort || { createdAt: -1 },
        limit: options?.limit,
        skip: options?.skip
      })
      
      // Append or replace articles based on mode
      if (options?.append) {
        setArticles(prev => [...prev, ...list])
        setHasMore(articles.length + list.length < total)
      } else {
        setArticles(list)
        setHasMore(list.length < total)
      }
      
      setTotal(total)
      console.log("Articles fetched:", list)
    } catch (error) {
      console.error("Failed to fetch articles:", error)
    } finally {
      setLoading(false)
    }
  }

  const createArticle = async (data: Omit<Article, "_id" | "createdAt" | "updatedAt">) => {
    try {
      const article = await lumi.entities.articles.create(data)
      console.log("Article created:", article)
      await fetchArticles()
      return article
    } catch (error) {
      console.error("Failed to create article:", error)
      throw error
    }
  }

  const updateArticle = async (id: string, updates: Partial<Article>) => {
    if (typeof id !== "string") {
      throw new Error("ID must be a string")
    }
    try {
      const updated = await lumi.entities.articles.update(id, updates)
      console.log("Article updated:", updated)
      await fetchArticles()
      return updated
    } catch (error) {
      console.error("Failed to update article:", error)
      throw error
    }
  }

  const deleteArticle = async (id: string) => {
    if (typeof id !== "string" || id === "[object Object]") {
      throw new Error("Invalid ID: must be a string, not an object")
    }
    try {
      await lumi.entities.articles.delete(id)
      console.log("Article deleted:", id)
      await fetchArticles()
    } catch (error) {
      console.error("Failed to delete article:", error)
      throw error
    }
  }

  return {
    articles,
    total,
    loading,
    hasMore,
    fetchArticles,
    createArticle,
    updateArticle,
    deleteArticle
  }
}
